import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '../../../stores/tenant.store';
import { useAcademic } from '../../../hooks/use-academic';
import { DocumentData } from 'firebase/firestore';

interface DeleteTermDialogProps {
  open: boolean;
  onClose: () => void;
  term: DocumentData | null;
  /** Optional — enables the impact preview (subjects + enrollments to be removed). */
  sections?: DocumentData[];
}

export function DeleteTermDialog({ open, onClose, term, sections = [] }: DeleteTermDialogProps) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const { hardDeleteTerm, isHardDeletingTerm } = useAcademic();
  const [error, setError] = useState<string | null>(null);

  // Impact preview — every section in the term (incl. cancelled) is removed,
  // and each section removal cascades its enrollments.
  const termSections = useMemo(
    () => (term ? sections.filter((s) => s.termId === term.id) : []),
    [sections, term],
  );
  const cancelledCount = useMemo(
    () => termSections.filter((s) => s.status === 'cancelled').length,
    [termSections],
  );
  const enrolledCount = useMemo(
    () => termSections.reduce((sum, s) => sum + (s.currentEnrollment ?? 0), 0),
    [termSections],
  );

  const handleDelete = async () => {
    if (!currentTenantId || !term) return;
    setError(null);
    try {
      await hardDeleteTerm({
        tenantId: currentTenantId,
        termId: term.id,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || t('sections.deleteDialog.error'));
    }
  };

  if (!term) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('sections.deleteDialog.title')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography sx={{ mb: 2 }}>
          {t('sections.deleteDialog.confirm')} <strong>{term.name}</strong>?
        </Typography>
        {(termSections.length > 0 || enrolledCount > 0) && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t(
              cancelledCount > 0
                ? 'sections.deleteDialog.impactWithCancelled'
                : 'sections.deleteDialog.impact',
              {
                subjects: termSections.length,
                cancelled: cancelledCount,
                enrolled: enrolledCount,
              },
            )}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary">
          {t('sections.deleteDialog.irreversible')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isHardDeletingTerm}>
          {t('sections.deleteDialog.cancel')}
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={isHardDeletingTerm}
        >
          {isHardDeletingTerm
            ? t('sections.deleteDialog.deleting')
            : t('sections.deleteDialog.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
